import { APP_INITIALIZER, Inject, Injectable, Provider } from '@angular/core';
import { WINDOW } from '@platform-frontend/util';
import { interval, Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { Popup } from './model/popup';
import { PopupCommunicationMessage } from './model/popup-communication-message';
import { PopupFeatures } from './model/popup-features';
import { PopupWithId } from './model/popup-with-id';

@Injectable({
  providedIn: 'root'
})
export class PopupService {
  constructor(@Inject(WINDOW) private window: Window) {}

  private popups: { [identifier: string]: Popup } = {};

  private popupClosed$: Subject<string> = new Subject<string>();

  init(): void {
    this.window.addEventListener('message', event => this.handlePopupMessageEvents(event));
    this.window.addEventListener('beforeunload', () => this.closeAll());
  }

  private handlePopupMessageEvents(event: MessageEvent): void {
    if (this.isYattaPopupEvent(event)) {
      const identifier = (event.source as Window | null)?.name;
      if (identifier && identifier in this.popups) this.handlePopupMessages(event.data, { ...this.popups[identifier], identifier });
    }
  }

  private isYattaPopupEvent(event: MessageEvent): event is MessageEvent<PopupCommunicationMessage> {
    const data = event.data;
    return data && data.source === 'popup';
  }

  private handlePopupMessages(message: PopupCommunicationMessage, popupWithId: PopupWithId): void {
    if (message.action === 'loaded' && popupWithId.closeOnLoadedMessageTimeStamp && message.timeStamp >= popupWithId.closeOnLoadedMessageTimeStamp) {
      this.close(popupWithId.identifier);
    }
  }

  private closeAll(): void {
    Object.keys(this.popups).forEach(identifier => this.closeWithoutSettingFocus(identifier));
  }

  openOrNavigate(identifier: string, url: string, features?: PopupFeatures, closeOnLoadedMessage: boolean = false): void {
    this.popups[identifier] = {
      closeOnLoadedMessageTimeStamp: closeOnLoadedMessage ? Date.now() : null,
      window: this.window.open(url, identifier, features?.get())
    };
  }

  closedByUser(identifier: string): Observable<unknown> {
    const popupClosedIntentionally$ = this.popupClosed$.pipe(filter(closedPopup => closedPopup === identifier));

    return interval(50).pipe(
      filter(() => this.popups[identifier].window?.closed ?? true),
      take(1),
      takeUntil(popupClosedIntentionally$)
    );
  }

  close(identifier: string): void {
    if (this.closeWithoutSettingFocus(identifier)) {
      this.window.focus();
    }
  }

  private closeWithoutSettingFocus(identifier: string): boolean {
    if (identifier in this.popups) {
      this.popupClosed$.next(identifier);
      this.popups[identifier].window?.close();
      delete this.popups[identifier];
      return true;
    }
    return false;
  }
}

export const PopupServiceInitializer: Provider = {
  provide: APP_INITIALIZER,
  useFactory: (popupService: PopupService) => () => popupService.init(),
  deps: [PopupService],
  multi: true
};
